// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DATE_INPUT_MASK,
  DATE_INPUT_PLACEHOLDER_KEY,
  DATE_TIME_INPUT_MASK,
  DATE_TIME_INPUT_PLACEHOLDER_KEY,
  formatDateTimeToMask,
  formatDateToMask,
  getDefaultDateInputFormat,
  normalizeDateInput,
  resolveMaskedDateTimeValue,
  resolveMaskedDateValue,
  toIsoDateString,
} from '@shared/ui-kit/utils/date-input';

export type DateInputGranularity = 'date' | 'datetime';

export type UseDateInputOptions = {
  value?: Date | string | null;
  onChange?: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  minDate?: Date | string;
  maxDate?: Date | string;
  /**
   * When `datetime`, value is `YYYY-MM-DDTHH:mm:ss`; time is optional and defaults
   * to `00:00`.
   */
  granularity?: DateInputGranularity;
};

export const useDateInput = ({
  value,
  onChange,
  onBlur,
  minDate,
  maxDate,
  granularity = 'date',
}: UseDateInputOptions) => {
  const includeTime = granularity === 'datetime';
  const { i18n, t } = useTranslation();
  const dateFormat = getDefaultDateInputFormat(i18n.language);
  const placeholder = t(
    includeTime ? DATE_TIME_INPUT_PLACEHOLDER_KEY : DATE_INPUT_PLACEHOLDER_KEY,
    { ns: 'common' },
  );

  const externalIso = useMemo(() => {
    if (typeof value === 'string') return value;

    const normalized = normalizeDateInput(value);

    return normalized ? toIsoDateString(normalized) : '';
  }, [value]);

  const lastValidIsoRef = useRef(externalIso);

  const externalDisplay = useMemo(
    () =>
      includeTime
        ? formatDateTimeToMask(value, dateFormat)
        : formatDateToMask(value, dateFormat),
    [dateFormat, includeTime, value],
  );

  const [displayValue, setDisplayValue] = useState(externalDisplay);

  useEffect(() => {
    setDisplayValue(externalDisplay);
    lastValidIsoRef.current = externalIso;
  }, [externalDisplay, externalIso]);

  const resolveValue = useCallback(
    (masked: string) =>
      includeTime
        ? resolveMaskedDateTimeValue(masked, dateFormat, minDate, maxDate)
        : resolveMaskedDateValue(masked, dateFormat, minDate, maxDate),
    [dateFormat, includeTime, maxDate, minDate],
  );

  const formatLastValid = useCallback(
    (iso: string) =>
      includeTime
        ? formatDateTimeToMask(iso, dateFormat)
        : formatDateToMask(iso, dateFormat),
    [dateFormat, includeTime],
  );

  const handleValueChange = useCallback(
    (values: { formattedValue: string; value: string }) => {
      const { formattedValue } = values;

      setDisplayValue(formattedValue);

      const resolved = resolveValue(formattedValue);

      if (resolved === null) return;

      if (resolved === lastValidIsoRef.current) return;

      lastValidIsoRef.current = resolved;
      onChange?.(resolved);
    },
    [onChange, resolveValue],
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    event => {
      const resolved = resolveValue(displayValue);

      if (resolved === null && displayValue.trim() !== '') {
        setDisplayValue(formatLastValid(lastValidIsoRef.current));
      }

      onBlur?.(event);
    },
    [displayValue, formatLastValid, onBlur, resolveValue],
  );

  return {
    displayValue,
    handleBlur,
    handleValueChange,
    mask: includeTime ? DATE_TIME_INPUT_MASK : DATE_INPUT_MASK,
    placeholder,
  };
};
